import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ReloadPerformanceDay, ReloadPeriodSummary, ReloadRun } from "@repo/shared";

import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import { formatSeconds } from "@/lib/duration-format";
import { cn } from "@/lib/utils";
import { reloadStatusLabel } from "./labels";

// The newest runs the chart draws; the list below keeps the rest.
const RUNS_CHART_MAX = 200;
const FAILED = new Set(["FAILED", "EXCEEDED_LIMIT"]);

function ReferenceLine({
  at,
  label,
  className,
}: {
  at: string;
  label?: string;
  className: string;
}) {
  return (
    <>
      <div className={cn("absolute inset-x-0 border-t-2", className)} style={{ bottom: at }} />
      {label && (
        <span
          className="absolute right-1 bg-surface px-1 font-mono text-[10.5px] text-dim"
          style={{ bottom: `calc(${at} + 2px)` }}
        >
          {label}
        </span>
      )}
    </>
  );
}

// One column per run, oldest to newest, failed ones in the danger tone, with
// the period's median and p90 of the succeeded runs as reference lines.
export function RunsChart({
  runs,
  medianSeconds,
  p90Seconds,
}: {
  runs: ReloadRun[]; // newest first, as the API sends them
  medianSeconds: number | null;
  p90Seconds: number | null;
}) {
  const { t } = useTranslation("reloads");
  const { formatDate, formatDateTime } = useFormatDate();

  const finished = runs.filter((r) => r.startTime && r.endTime);
  const points = finished
    .slice(0, RUNS_CHART_MAX)
    .reverse()
    .map((r) => ({
      id: r.reloadId,
      at: r.startTime as string,
      status: r.status,
      seconds: (Date.parse(r.endTime as string) - Date.parse(r.startTime as string)) / 1000,
    }));
  if (points.length === 0) return null;

  const top = Math.max(...points.map((p) => p.seconds), p90Seconds ?? 0) || 1;
  const pct = (seconds: number) => `${Math.min(100, (seconds / top) * 100)}%`;
  const failed = points.filter((p) => FAILED.has(p.status)).length;
  const fmt = (seconds: number | null) => (seconds === null ? "—" : formatSeconds(seconds));
  // Lines closer than ~20px (15% of the 9rem plot) would print their labels over
  // each other: the p90 line then carries both.
  const labelsMerged =
    medianSeconds !== null && p90Seconds !== null && (p90Seconds - medianSeconds) / top < 0.15;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-hairline p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <h3 className="text-sm font-semibold text-ink">{t("runs.chart.title")}</h3>
          <p className="text-xs text-dim">
            {t("runs.chart.subtitle")}
            {finished.length > RUNS_CHART_MAX &&
              ` ${t("runs.chart.newest", { count: RUNS_CHART_MAX })}`}
          </p>
        </div>
        <div className="flex gap-3 font-mono text-[11px] text-dim" aria-hidden>
          <span className="inline-flex items-center gap-1.5">
            <i className="size-2.5 rounded-sm bg-brand" />
            {t("runs.chart.succeeded")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="size-2.5 rounded-sm bg-status-danger" />
            {t("runs.chart.failed")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="w-3.5 border-t-2 border-primary" />
            {t("runs.chart.median")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="w-3.5 border-t-2 border-dashed border-status-warning" />
            p90
          </span>
        </div>
      </div>
      <div
        role="img"
        aria-label={t("runs.chart.summary", {
          count: points.length,
          failed,
          median: fmt(medianSeconds),
          p90: fmt(p90Seconds),
        })}
        className="relative h-36 border-b border-l border-hairline"
      >
        <div className="absolute inset-x-1 inset-y-0 flex items-end gap-px">
          {points.map((p) => (
            <span
              key={p.id}
              title={`${formatDateTime(p.at)} · ${reloadStatusLabel(t, p.status)} · ${formatSeconds(p.seconds)}`}
              className={cn(
                "min-w-0.5 flex-1 rounded-t-[2px]",
                FAILED.has(p.status) ? "bg-status-danger" : "bg-brand",
              )}
              style={{ height: pct(p.seconds) }}
            />
          ))}
        </div>
        {medianSeconds !== null && (
          <ReferenceLine
            at={pct(medianSeconds)}
            label={
              labelsMerged ? undefined : `${t("runs.chart.median")} ${formatSeconds(medianSeconds)}`
            }
            className="border-primary"
          />
        )}
        {p90Seconds !== null && (
          <ReferenceLine
            at={pct(p90Seconds)}
            label={
              labelsMerged
                ? t("runs.chart.both", { median: fmt(medianSeconds), p90: fmt(p90Seconds) })
                : `p90 ${formatSeconds(p90Seconds)}`
            }
            className="border-dashed border-status-warning"
          />
        )}
      </div>
      <div className="flex justify-between font-mono text-[10.5px] text-dim" aria-hidden>
        <span>{formatDate(points[0].at)}</span>
        <span>{formatDate(points[points.length - 1].at)}</span>
      </div>
    </section>
  );
}

// A YYYY-MM-DD day of the viewer's zone, as a local date.
const dayDate = (day: string) => new Date(`${day}T00:00:00`);

function ChartCard({
  title,
  subtitle,
  legend,
  children,
}: {
  title: string;
  subtitle: string;
  legend?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-hairline bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <h3 className="text-sm font-semibold text-ink">{title}</h3>
          <p className="text-xs text-dim">{subtitle}</p>
        </div>
        {legend && (
          <div className="flex gap-3 font-mono text-[11px] text-dim" aria-hidden>
            {legend}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function DayAxis({ days }: { days: { day: string }[] }) {
  const { formatDate } = useFormatDate();
  if (days.length === 0) return null;
  const middle = days[Math.floor((days.length - 1) / 2)];
  return (
    <div className="flex justify-between font-mono text-[10.5px] text-dim" aria-hidden>
      <span>{formatDate(dayDate(days[0].day))}</span>
      {days.length > 2 && <span>{formatDate(dayDate(middle.day))}</span>}
      <span>{formatDate(dayDate(days[days.length - 1].day))}</span>
    </div>
  );
}

// Hours of finished reloads per day, succeeded and failed stacked, with the
// period's daily average.
export function DailyHoursChart({ days }: { days: ReloadPerformanceDay[] }) {
  const { t } = useTranslation("reloads");
  const { language } = useLocale();
  const { formatDate } = useFormatDate();

  const hours = (seconds: number) =>
    (seconds / 3600).toLocaleString(language, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  const totals = days.map((d) => d.succeededSeconds + d.failedSeconds);
  const top = Math.max(...totals, 1);
  const average = totals.reduce((sum, v) => sum + v, 0) / Math.max(days.length, 1);
  const pct = (seconds: number) => `${(seconds / top) * 100}%`;

  return (
    <ChartCard
      title={t("performance.hours.title")}
      subtitle={t("performance.hours.subtitle")}
      legend={
        <>
          <span className="inline-flex items-center gap-1.5">
            <i className="size-2.5 rounded-sm bg-brand" />
            {t("runs.chart.succeeded")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="size-2.5 rounded-sm bg-status-danger" />
            {t("runs.chart.failed")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="w-3.5 border-t-2 border-primary" />
            {t("performance.hours.averageLegend")}
          </span>
        </>
      }
    >
      <div
        role="img"
        aria-label={t("performance.hours.summary", {
          count: days.length,
          average: hours(average),
          top: hours(top),
        })}
        className="relative h-48 border-b border-l border-hairline"
      >
        <div className="absolute inset-x-1 inset-y-0 flex items-end gap-0.5">
          {days.map((d) => (
            <span
              key={d.day}
              title={t("performance.hours.day", {
                day: formatDate(dayDate(d.day)),
                total: hours(d.succeededSeconds + d.failedSeconds),
                failed: hours(d.failedSeconds),
                count: d.count,
              })}
              className="flex min-w-0.5 flex-1 flex-col-reverse overflow-hidden rounded-t-[2px]"
              style={{ height: pct(d.succeededSeconds + d.failedSeconds) }}
            >
              <span className="bg-brand" style={{ flex: `${d.succeededSeconds} 1 0` }} />
              <span className="bg-status-danger" style={{ flex: `${d.failedSeconds} 1 0` }} />
            </span>
          ))}
        </div>
        {average > 0 && (
          <ReferenceLine
            at={pct(average)}
            label={t("performance.hours.average", { value: hours(average) })}
            className="border-primary"
          />
        )}
      </div>
      <DayAxis days={days} />
    </ChartCard>
  );
}

// One day series as an SVG line on its own scale; a day without a value
// breaks the line instead of dropping to zero.
function DayLine({
  label,
  values,
  className,
}: {
  label: string;
  values: (number | null)[];
  className: string;
}) {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min || 1;
  const x = (i: number) => (values.length > 1 ? (i / (values.length - 1)) * 300 : 150);
  const y = (v: number) => 60 - ((v - min) / span) * 60;
  let path = "";
  let pen = false;
  values.forEach((v, i) => {
    if (v === null) {
      pen = false;
      return;
    }
    path += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
    pen = true;
  });

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium text-ink">{label}</span>
        <span className="font-mono text-dim">
          {formatSeconds(present[0])} → {formatSeconds(present[present.length - 1])}
        </span>
      </div>
      <div className="relative ml-14 h-16 border-b border-l border-hairline">
        <span className="absolute top-0 -left-14 font-mono text-[10.5px] text-dim">
          {formatSeconds(max)}
        </span>
        <span className="absolute bottom-0 -left-14 font-mono text-[10.5px] text-dim">
          {formatSeconds(min)}
        </span>
        <svg
          viewBox="0 0 300 60"
          preserveAspectRatio="none"
          className="absolute inset-1 h-[calc(100%-0.5rem)] w-[calc(100%-0.5rem)] overflow-visible"
          aria-hidden
        >
          <path
            d={path}
            fill="none"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            className={className}
          />
        </svg>
      </div>
    </div>
  );
}

// The median and p90 of succeeded reloads per day, each on its own scale (a
// shared one would flatten the median against the p90).
export function DailyDurationChart({
  days,
  summary,
}: {
  days: ReloadPerformanceDay[];
  summary: ReloadPeriodSummary;
}) {
  const { t } = useTranslation("reloads");
  const fmt = (seconds: number | null) => (seconds === null ? "—" : formatSeconds(seconds));

  return (
    <ChartCard
      title={t("performance.duration.title")}
      subtitle={t("performance.duration.subtitle")}
    >
      <div
        role="img"
        aria-label={t("performance.duration.summary", {
          median: fmt(summary.medianSeconds),
          p90: fmt(summary.p90Seconds),
        })}
        className="flex flex-col gap-4"
      >
        <DayLine
          label={t("runs.chart.median")}
          values={days.map((d) => d.medianSeconds)}
          className="stroke-primary"
        />
        <DayLine
          label="p90"
          values={days.map((d) => d.p90Seconds)}
          className="stroke-status-warning"
        />
      </div>
      <div className="ml-14">
        <DayAxis days={days} />
      </div>
    </ChartCard>
  );
}
